if (!customElements.get('if-landing-carousel')) {
  customElements.define('if-landing-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
      this.controls = this.querySelector('[data-carousel-controls]');
      this.dots = [];
      this.index = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => this.start(), options);
      this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.go(this.index - 1, true), options);
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.go(this.index + 1, true), options);
      this.controls?.addEventListener('click', event => {
        const dot = event.target.closest('[data-carousel-dot]');
        const index = this.dots.indexOf(dot);
        if (index >= 0) this.go(index, true);
      }, options);
      this.bindDragging(options);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.stop(); }, options);
      this.addEventListener('mouseleave', () => { this.hovered = false; this.start(); }, options);
      this.addEventListener('focusin', event => {
        this.stop();
        if (this.dataset.productViews !== 'true') return;
        const slide = event.target.closest('[data-carousel-slide]');
        if (!slide || !this.slides.includes(slide)) return;
        requestAnimationFrame(() => {
          if (!this.isConnected || this.gesture) return;
          this.viewport.scrollTo({ left: slide.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left + this.viewport.scrollLeft, behavior: 'instant' });
        });
      }, options);
      this.addEventListener('focusout', () => requestAnimationFrame(() => this.start()), options);
      document.addEventListener('visibilitychange', () => this.start(), options);
      this.addEventListener('keydown', event => {
        if (event.target !== this.viewport) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      }, options);
      this.closest('.shopify-section')?.addEventListener('shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (selected >= 0) this.go(selected, true);
        this.stop();
      }, options);
      if (this.dataset.mode === 'scroll' && this.viewport) {
        this.viewport.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimeout);
          this.scrollTimeout = setTimeout(() => {
            if (this.gesture) return;
            this.index = this.closestIndex();
            this.update();
          }, 100);
        }, options);
      }
      this.resizeObserver = new ResizeObserver(() => {
        this.index = Math.min(this.index, this.lastIndex());
        if (this.dataset.adaptive === 'true' && this.viewport && this.viewportWidth !== this.viewport.clientWidth) {
          this.viewportWidth = this.viewport.clientWidth;
          const slide = this.slides[this.index];
          if (slide) this.viewport.scrollTo({ left: slide.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left + this.viewport.scrollLeft, behavior: 'instant' });
        }
        this.update();
      });
      this.resizeObserver.observe(this);
      if (this.dataset.adaptive === 'true') this.slides.forEach(slide => this.resizeObserver.observe(slide));
      this.update();
      this.start();
    }
    lastIndex() {
      if (this.dataset.mode !== 'scroll' || !this.viewport || !this.slides[0]) return Math.max(0, this.slides.length - 1);
      if (this.viewport.scrollWidth <= this.viewport.clientWidth + 2) return 0;
      const gap = parseFloat(getComputedStyle(this.viewport).columnGap) || 0;
      const visible = Math.max(1, Math.floor((this.viewport.clientWidth + gap + 2) / (this.slides[0].getBoundingClientRect().width + gap)));
      return Math.max(0, this.slides.length - visible);
    }
    closestIndex() {
      const left = this.viewport.getBoundingClientRect().left;
      let closest = 0;
      this.slides.forEach((slide, i) => {
        if (Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(this.slides[closest].getBoundingClientRect().left - left)) closest = i;
      });
      return Math.min(closest, this.lastIndex());
    }
    bindDragging(options) {
      if (!this.viewport) return;
      this.viewport.addEventListener('pointerdown', event => {
        if (this.lastIndex() < 1 || (event.pointerType === 'mouse' && event.button !== 0)) return;
        if (this.dataset.mode === 'scroll' && event.pointerType !== 'mouse') return;
        const interactive = event.target.closest('button, input, select, textarea, [contenteditable="true"]');
        if (interactive && !(this.dataset.dragMedia === 'true' && interactive.closest('modal-opener'))) return;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.viewport.scrollLeft, horizontal: false };
        this.stop();
      }, options);
      this.viewport.addEventListener('pointermove', event => {
        const gesture = this.gesture;
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (!gesture.horizontal) {
          if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { this.cancelGesture(); return; }
          if (Math.abs(dx) < 12 || Math.abs(dx) <= Math.abs(dy)) return;
          gesture.horizontal = true;
          this.classList.add('is-dragging');
          this.viewport.setPointerCapture?.(event.pointerId);
          if (this.dataset.mode === 'scroll') {
            gesture.snap = this.viewport.style.scrollSnapType;
            gesture.behavior = this.viewport.style.scrollBehavior;
            this.viewport.style.scrollSnapType = 'none';
            this.viewport.style.scrollBehavior = 'auto';
          }
        }
        if (event.cancelable) event.preventDefault();
        if (this.dataset.mode === 'scroll') this.viewport.scrollLeft = gesture.left - dx;
      }, options);
      this.viewport.addEventListener('pointerup', event => {
        const gesture = this.gesture;
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x;
        const threshold = Math.max(32, Math.min(80, this.viewport.clientWidth * .1));
        const next = this.dataset.mode === 'scroll' ? this.closestIndex() : this.index + (dx < 0 ? 1 : -1);
        const navigate = gesture.horizontal && (this.dataset.mode === 'scroll' || Math.abs(dx) >= threshold);
        this.cancelGesture();
        if (navigate) this.go(next, true);
      }, options);
      this.viewport.addEventListener('pointercancel', () => this.cancelGesture(), options);
      this.viewport.addEventListener('lostpointercapture', () => this.cancelGesture(), options);
      this.viewport.addEventListener('dragstart', event => { if (this.gesture) event.preventDefault(); }, options);
      this.viewport.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.suppressClickUntil = 0;
        }
      }, { ...options, capture: true });
    }
    cancelGesture() {
      const gesture = this.gesture;
      if (!gesture) return;
      this.gesture = null;
      if (gesture.horizontal) this.suppressClickUntil = performance.now() + 400;
      if (gesture.snap !== undefined) this.viewport.style.scrollSnapType = gesture.snap;
      if (gesture.behavior !== undefined) this.viewport.style.scrollBehavior = gesture.behavior;
      if (this.viewport.hasPointerCapture?.(gesture.id)) this.viewport.releasePointerCapture(gesture.id);
      this.classList.remove('is-dragging');
      this.start();
    }
    go(next, manual = false) {
      const count = this.lastIndex() + 1;
      this.index = (next + count) % count;
      if (this.dataset.mode === 'scroll') {
        const slide = this.slides[this.index];
        if (slide) this.viewport.scrollTo({ left: slide.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left + this.viewport.scrollLeft, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
      } else {
        this.slides.forEach((slide, index) => {
          slide.hidden = index !== this.index;
          slide.classList.toggle('is-active', index === this.index);
        });
      }
      this.update(manual);
      this.start();
    }
    update(manual = false) {
      const last = this.lastIndex();
      if (this.dataset.productViews === 'true') this.slides.forEach((slide, index) => {
        slide.inert = index !== this.index;
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      if (this.dataset.adaptive === 'true' && this.viewport && this.slides[this.index]) {
        this.viewport.style.height = Math.ceil(this.slides[this.index].getBoundingClientRect().height) + 'px';
      }
      this.viewport?.toggleAttribute('data-carousel-draggable', last > 0);
      if (this.controls) {
        this.controls.hidden = last < 1;
        const count = last > 0 ? last + 1 : 0;
        if (this.dots.length !== count) {
          this.dots = Array.from({ length: count }, (_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'if-landing-dot';
            dot.setAttribute('data-carousel-dot', '');
            dot.setAttribute('aria-label', `${this.controls.dataset.slideLabel} ${index + 1}`);
            dot.setAttribute('aria-controls', this.controls.dataset.viewportId);
            return dot;
          });
          this.controls.replaceChildren(...this.dots);
        }
      }
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
    }
    stop() { clearTimeout(this.timer); }
    start() {
      this.stop();
      if (!this.isConnected || this.lastIndex() < 1 || this.dataset.autoplay !== 'true' || this.gesture || this.hovered || this.contains(document.activeElement) || this.reducedMotion.matches || document.hidden || window.Shopify?.designMode) return;
      this.timer = setTimeout(() => this.go(this.index + 1), Math.max(3000, Number(this.dataset.delay) || 5000));
    }
    disconnectedCallback() {
      this.cancelGesture();
      this.stop();
      clearTimeout(this.scrollTimeout);
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
